import { refusalKey } from './refusal-key';

/**
 * The profile the "Mein Profil" page loads, the backend's refusal codes, and which text a refused
 * save shows — kept out of the element so it is testable without a DOM. Which changes may be saved
 * lives with the form (`own-profile-form.ts`).
 */

export interface OwnProfile {
    uid: string;
    username: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
}

/**
 * The backend's refusal codes of the profile save: `update.user` (UpdateUserCommandHandler) and
 * `change.own-email-address` (ChangeOwnEmailAddressCommandHandler); both share the "not your record"
 * code (OwnUserRecordTrait), the e-mail change the current-password codes (CurrentPasswordCheckTrait).
 */
export const OWN_PROFILE_ERROR_CODES = {
    otherUser: 1792742400011,
    emptyName: 1792742400012,
    invalidEmail: 1792742400013,
    emailTaken: 1792742400014,
    wrongPassword: 1792742400015,
    invalidName: 1792742400016,
    nothingToChange: 1792742400017,
    /** The brake on password entries (CurrentPasswordCheckTrait): too many wrong passwords, locked for a while. */
    passwordChecksLocked: 1792742400018,
} as const;

export type OwnProfileErrorKey = keyof typeof OWN_PROFILE_ERROR_CODES | 'forbidden' | 'failed';

/**
 * The refusal codes of `change.own-password` (ChangeOwnPasswordCommandHandler, PasswordService): the
 * shared ones of the own-record rule and the current-password check, and the new password's own.
 */
export const OWN_PASSWORD_ERROR_CODES = {
    otherUser: OWN_PROFILE_ERROR_CODES.otherUser,
    wrongPassword: OWN_PROFILE_ERROR_CODES.wrongPassword,
    passwordChecksLocked: OWN_PROFILE_ERROR_CODES.passwordChecksLocked,
    passwordRuleNotMet: 1792742400019,
    passwordUnchanged: 1792742400020,
} as const;

export type OwnPasswordErrorKey = keyof typeof OWN_PASSWORD_ERROR_CODES | 'forbidden' | 'failed';

export class OwnProfileState {

    /** Which text a refused profile save shows: the backend's code when known, 403 as "not allowed", else general. */
    public static errorKey(error: unknown): OwnProfileErrorKey {
        return refusalKey(error, OWN_PROFILE_ERROR_CODES);
    }

    /** Which text a refused password change shows; the same rules as `errorKey`, over the password codes. */
    public static passwordErrorKey(error: unknown): OwnPasswordErrorKey {
        return refusalKey(error, OWN_PASSWORD_ERROR_CODES);
    }
}
